"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { Lock, Shield, TrendingUp, Newspaper, X } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { trackEvent } from "@/lib/posthog";

type FundPaywallModalProps = {
  open: boolean;
  onClose: () => void;
};

const PRO_FEATURES = [
  { icon: Shield, label: "Full Risk & Volatility Analysis" },
  { icon: TrendingUp, label: "Complete WTM Fund Assessment" },
  { icon: Newspaper, label: "Fund News & Sentiment Analysis" },
];

export function FundPaywallModal({ open, onClose }: FundPaywallModalProps) {
  const router = useRouter();

  // Funnel: fund-report Pro wall shown (free user hit sections 4-8).
  useEffect(() => {
    if (open) trackEvent("paywall_shown", { surface: "fund_report" });
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-navy/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-surface border border-surface-light/30 rounded-2xl p-6 md:p-8 max-w-md w-full shadow-xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 text-muted-foreground hover:text-foreground transition cursor-pointer border-none bg-transparent"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center">
            <Lock className="w-6 h-6 text-gold" />
          </div>
        </div>

        {/* Heading */}
        <h2 className="text-foreground text-xl font-serif text-center mb-2">
          Unlock Full Fund Analysis
        </h2>
        <p className="text-muted-foreground text-sm text-center mb-6">
          Get the complete picture with our PRO plan. Sections 4-8 are exclusive
          to PRO members.
        </p>

        {/* Feature list */}
        <div className="space-y-3 mb-6">
          {PRO_FEATURES.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue/10 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-blue" />
              </div>
              <span className="text-foreground text-sm">{label}</span>
            </div>
          ))}
        </div>

        {/* CTA buttons */}
        <div className="space-y-3">
          <button
            onClick={() => {
              trackEvent("upgrade_clicked", { surface: "fund_report", destination: "/pricing" });
              router.push("/pricing");
            }}
            className={cn(
              "w-full py-2.5 rounded-lg font-semibold text-sm transition cursor-pointer border-none",
              "bg-blue text-white hover:opacity-90"
            )}
          >
            Upgrade to Pro
          </button>
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-lg font-semibold text-sm transition cursor-pointer border-none bg-transparent text-muted-foreground hover:text-foreground"
          >
            Continue with Free
          </button>
        </div>
      </div>
    </div>
  );
}
